import { useCallback, useEffect, useRef, useState } from "react";
import {
  Canvas,
  FabricImage,
  FabricObject,
  Rect,
  Textbox,
  filters,
  setFilterBackend,
  Canvas2dFilterBackend,
} from "fabric";
import { removeBackground } from "@imgly/background-removal";

// Filters are computed per-pixel on the 2D canvas backend so our custom
// Levels filter (which has no WebGL shader) behaves the same as the stock ones.
setFilterBackend(new Canvas2dFilterBackend());

const clamp255 = (value: number) => Math.min(255, Math.max(0, value));

class LevelsFilter extends (filters.BaseFilter as any) {
  static type = "Levels";

  constructor(options: { blackPoint?: number; whitePoint?: number } = {}) {
    super(options);
    (this as any).blackPoint = options.blackPoint ?? 0;
    (this as any).whitePoint = options.whitePoint ?? 255;
  }

  isNeutralState() {
    return (this as any).blackPoint === 0 && (this as any).whitePoint === 255;
  }

  applyTo2d({ imageData: { data } }: { imageData: ImageData }) {
    const black = (this as any).blackPoint;
    const range = Math.max(1, (this as any).whitePoint - black);
    for (let i = 0; i < data.length; i += 4) {
      data[i] = clamp255(((data[i] - black) / range) * 255);
      data[i + 1] = clamp255(((data[i + 1] - black) / range) * 255);
      data[i + 2] = clamp255(((data[i + 2] - black) / range) * 255);
    }
  }
}

export type AdjustmentValues = {
  contrast: number; // -1..1
  brightness: number; // -1..1
  saturation: number; // -1..1
  hue: number; // -180..180 (degrees)
  blackPoint: number; // 0..255
  whitePoint: number; // 0..255
};

export const defaultAdjustments: AdjustmentValues = {
  contrast: 0,
  brightness: 0,
  saturation: 0,
  hue: 0,
  blackPoint: 0,
  whitePoint: 255,
};

export type TextStyle = {
  fontSize: number;
  fontFamily: string;
  fill: string;
  fontWeight: "normal" | "bold";
  fontStyle: "normal" | "italic";
};

export const fontFamilies = [
  "Arial",
  "Georgia",
  "Times New Roman",
  "Courier New",
  "Verdana",
  "Comic Sans MS",
];

// On-screen size cap: the canvas backstore always matches the current
// working image's native pixels (for 1:1 crop/filter/export math); these
// just bound how large it's allowed to render on screen.
const CSS_MAX_WIDTH = 560;
const CSS_MAX_HEIGHT = 420;
const BASE_IMAGE_KEY = "isBaseImage";

// Resizes the canvas backstore to the image's own pixel dimensions (so
// canvasElRef always reflects the current working image's real size),
// scales the on-screen display to fit within the panel via CSS only, and
// places the image to exactly fill that backstore at scale 1.
const fitCanvasToImage = (canvas: Canvas, img: FabricImage) => {
  const width = img.width || 1;
  const height = img.height || 1;
  canvas.setDimensions({ width, height }, { backstoreOnly: true });
  const cssScale = Math.min(CSS_MAX_WIDTH / width, CSS_MAX_HEIGHT / height, 1);
  canvas.setDimensions(
    { width: width * cssScale, height: height * cssScale },
    { cssOnly: true },
  );
  img.set({
    left: 0,
    top: 0,
    originX: "left",
    originY: "top",
    scaleX: 1,
    scaleY: 1,
    selectable: false,
    evented: false,
  });
  (img as any)[BASE_IMAGE_KEY] = true;
  canvas.add(img);
  canvas.requestRenderAll();
};

const probeImage = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(url);
    img.onerror = () => reject(url);
    img.src = url;
  });

// Tries each candidate URL in order (e.g. large -> medium -> small) and
// resolves with the first one that actually loads. Falls back to the last
// candidate unconditionally so the editor always has something to open.
const resolveBestSrc = async (sources: string[]): Promise<string> => {
  for (let i = 0; i < sources.length - 1; i++) {
    try {
      return await probeImage(sources[i]);
    } catch {
      // try the next, smaller candidate
    }
  }
  return sources[sources.length - 1];
};

export const useImageEditor = (imageSources: string[], open: boolean) => {
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);
  const fabricCanvasRef = useRef<Canvas | null>(null);
  const historyRef = useRef<{ stack: string[]; index: number }>({
    stack: [],
    index: -1,
  });
  const restoringRef = useRef(false);
  const cropRectRef = useRef<Rect | null>(null);

  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [adjustments, setAdjustments] =
    useState<AdjustmentValues>(defaultAdjustments);
  const [selectedText, setSelectedText] = useState<TextStyle | null>(null);

  const getBaseImage = useCallback((): FabricImage | undefined => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return undefined;
    return canvas
      .getObjects()
      .find((o: FabricObject) => (o as any)[BASE_IMAGE_KEY]) as
      | FabricImage
      | undefined;
  }, []);

  const refreshHistoryFlags = useCallback(() => {
    const { stack, index } = historyRef.current;
    setCanUndo(index > 0);
    setCanRedo(index < stack.length - 1);
    setHasChanges(index > 0);
  }, []);

  const commit = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || restoringRef.current) return;
    const json = JSON.stringify(canvas.toJSON());
    const history = historyRef.current;
    if (history.stack[history.index] === json) return;
    history.stack = history.stack.slice(0, history.index + 1);
    history.stack.push(json);
    history.index = history.stack.length - 1;
    refreshHistoryFlags();
  }, [refreshHistoryFlags]);

  const restore = useCallback(
    (json: string) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      restoringRef.current = true;
      canvas.loadFromJSON(json).then(() => {
        canvas.renderAll();
        restoringRef.current = false;
        const base = getBaseImage();
        if (base) {
          const levels = (base.filters || []).find(
            (f: any) => f.type === "Levels",
          ) as any;
          const contrast = (base.filters || []).find(
            (f: any) => f.type === "Contrast",
          ) as any;
          const brightness = (base.filters || []).find(
            (f: any) => f.type === "Brightness",
          ) as any;
          const saturation = (base.filters || []).find(
            (f: any) => f.type === "Saturation",
          ) as any;
          const hue = (base.filters || []).find(
            (f: any) => f.type === "HueRotation",
          ) as any;
          setAdjustments({
            contrast: contrast?.contrast ?? 0,
            brightness: brightness?.brightness ?? 0,
            saturation: saturation?.saturation ?? 0,
            hue: hue ? hue.rotation * 180 : 0,
            blackPoint: levels?.blackPoint ?? 0,
            whitePoint: levels?.whitePoint ?? 255,
          });
        }
        setSelectedText(null);
      });
    },
    [getBaseImage],
  );

  const undo = useCallback(() => {
    const history = historyRef.current;
    if (history.index <= 0) return;
    history.index -= 1;
    restore(history.stack[history.index]);
    refreshHistoryFlags();
  }, [restore, refreshHistoryFlags]);

  const redo = useCallback(() => {
    const history = historyRef.current;
    if (history.index >= history.stack.length - 1) return;
    history.index += 1;
    restore(history.stack[history.index]);
    refreshHistoryFlags();
  }, [restore, refreshHistoryFlags]);

  const readSelectedText = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const active = canvas?.getActiveObject() as Textbox | undefined;
    if (active && active.type === "textbox") {
      setSelectedText({
        fontSize: active.fontSize || 24,
        fontFamily: active.fontFamily || fontFamilies[0],
        fill: (active.fill as string) || "#000000",
        fontWeight: (active.fontWeight as any) || "normal",
        fontStyle: (active.fontStyle as any) || "normal",
      });
    } else {
      setSelectedText(null);
    }
  }, []);

  // init / teardown canvas per open image
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    let rafId = 0;
    let disposeCanvas: (() => void) | undefined;

    // The <canvas> element may not be mounted yet on the same render pass
    // that flips `open` to true (e.g. while the modal portal is still
    // attaching), so keep checking until canvasElRef.current exists.
    const run = () => {
      if (cancelled) return;
      if (!canvasElRef.current) {
        rafId = requestAnimationFrame(run);
        return;
      }

      const canvas = new Canvas(canvasElRef.current, {
        backgroundColor: "#ffffff",
      });
      fabricCanvasRef.current = canvas;
      historyRef.current = { stack: [], index: -1 };
      setIsReady(false);
      setLoadError(false);
      setIsCropping(false);
      setIsBusy(false);
      setAdjustments(defaultAdjustments);
      setSelectedText(null);

      resolveBestSrc(imageSources)
        .then((resolvedSrc) =>
          FabricImage.fromURL(resolvedSrc, { crossOrigin: "anonymous" }),
        )
        .then((img) => {
          if (cancelled) return;
          fitCanvasToImage(canvas, img);
          commit();
          setIsReady(true);
        })
        .catch((error) => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.error("ImageEditor: failed to load image", error);
          setLoadError(true);
        });

      const handleModified = () => commit();
      const handleTextExit = () => commit();
      const handleSelection = () => readSelectedText();
      const handleSelectionCleared = () => setSelectedText(null);

      canvas.on("object:modified", handleModified);
      canvas.on("text:editing:exited", handleTextExit);
      canvas.on("selection:created", handleSelection);
      canvas.on("selection:updated", handleSelection);
      canvas.on("selection:cleared", handleSelectionCleared);

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== "Delete" && e.key !== "Backspace") return;
        const active = canvas.getActiveObject() as any;
        if (
          active &&
          active.type === "textbox" &&
          !active.isEditing &&
          active !== getBaseImage()
        ) {
          canvas.remove(active);
          canvas.requestRenderAll();
          commit();
        }
      };
      window.addEventListener("keydown", handleKeyDown);

      disposeCanvas = () => {
        window.removeEventListener("keydown", handleKeyDown);
        canvas.dispose();
        fabricCanvasRef.current = null;
      };
    };
    run();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      disposeCanvas?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, imageSources]);

  const applyAdjustments = useCallback(
    (values: AdjustmentValues, shouldCommit: boolean) => {
      setAdjustments(values);
      const base = getBaseImage();
      const canvas = fabricCanvasRef.current;
      if (!base || !canvas) return;
      base.filters = [
        new filters.Contrast({ contrast: values.contrast }),
        new filters.Brightness({ brightness: values.brightness }),
        new filters.Saturation({ saturation: values.saturation }),
        new filters.HueRotation({ rotation: values.hue / 180 }),
        new (LevelsFilter as any)({
          blackPoint: values.blackPoint,
          whitePoint: values.whitePoint,
        }),
      ];
      base.applyFilters();
      canvas.requestRenderAll();
      if (shouldCommit) commit();
    },
    [getBaseImage, commit],
  );

  const addText = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const text = new Textbox("Text", {
      left: canvas.getWidth() / 2,
      top: canvas.getHeight() / 2,
      originX: "center",
      originY: "center",
      fontSize: 24,
      fontFamily: fontFamilies[0],
      fill: "#000000",
      fontWeight: "normal",
      fontStyle: "normal",
      width: 150,
    });
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.requestRenderAll();
    readSelectedText();
    commit();
  }, [commit, readSelectedText]);

  const updateSelectedTextStyle = useCallback(
    (patch: Partial<TextStyle>) => {
      const canvas = fabricCanvasRef.current;
      const active = canvas?.getActiveObject() as Textbox | undefined;
      if (!canvas || !active || active.type !== "textbox") return;
      active.set(patch as any);
      canvas.requestRenderAll();
      readSelectedText();
      commit();
    },
    [commit, readSelectedText],
  );

  const startCrop = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const base = getBaseImage();
    if (!canvas || !base) return;
    const bounds = base.getBoundingRect();
    const rect = new Rect({
      left: bounds.width / 2,
      top: bounds.height / 2,
      width: bounds.width - 10,
      height: bounds.height - 10,
      fill: "rgba(0,0,0,0.15)",
      stroke: "#ffffff",
      strokeDashArray: [6, 4],
      strokeWidth: 2,
      lockRotation: true,
      hasRotatingPoint: false,
    } as any);
    (rect as any).lockRotation = true;
    rect.setControlsVisibility({ mtr: false });
    cropRectRef.current = rect;
    canvas.add(rect);
    canvas.setActiveObject(rect);
    canvas.requestRenderAll();
    setIsCropping(true);
  }, [getBaseImage]);

  const cancelCrop = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const rect = cropRectRef.current;
    if (canvas && rect) {
      canvas.remove(rect);
      canvas.requestRenderAll();
    }
    cropRectRef.current = null;
    setIsCropping(false);
  }, []);

  const applyCrop = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const rect = cropRectRef.current;
    if (!canvas || !rect) return;
    const width = rect.getScaledWidth();
    const height = rect.getScaledHeight();
    const left = (rect.left as number) - width / 2;
    const top = (rect.top as number) - height / 2;

    canvas.remove(rect);
    canvas.discardActiveObject();
    canvas.requestRenderAll();

    const croppedUrl = canvas.toDataURL({
      format: "png",
      multiplier: 1,
      left,
      top,
      width,
      height,
    });

    cropRectRef.current = null;
    setIsCropping(false);

    canvas.getObjects().forEach((o) => canvas.remove(o));

    FabricImage.fromURL(croppedUrl).then((img) => {
      fitCanvasToImage(canvas, img);
      setAdjustments(defaultAdjustments);
      commit();
    });
  }, [commit]);

  const runRemoveBackground = useCallback(async () => {
    const canvas = fabricCanvasRef.current;
    const base = getBaseImage();
    if (!canvas || !base) return;
    setIsBusy(true);
    try {
      const currentValues = adjustments;
      const sourceUrl = base.toDataURL({ format: "png", multiplier: 1 });
      const resultBlob = await removeBackground(sourceUrl);
      const resultUrl = URL.createObjectURL(resultBlob);
      const img = await FabricImage.fromURL(resultUrl);
      canvas.remove(base);
      fitCanvasToImage(canvas, img);
      canvas.sendObjectToBack(img);
      canvas.requestRenderAll();
      applyAdjustments(currentValues, false);
      commit();
    } finally {
      setIsBusy(false);
    }
  }, [getBaseImage, adjustments, applyAdjustments, commit]);

  const exportImage = useCallback((): string | null => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return null;
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    return canvas.toDataURL({ format: "png", quality: 1, multiplier: 1 });
  }, []);

  return {
    canvasElRef,
    isReady,
    loadError,
    canUndo,
    canRedo,
    hasChanges,
    isCropping,
    isBusy,
    adjustments,
    selectedText,
    undo,
    redo,
    applyAdjustments,
    addText,
    updateSelectedTextStyle,
    startCrop,
    applyCrop,
    cancelCrop,
    runRemoveBackground,
    exportImage,
  };
};
